export type FilterType =
  | 'and'
  | 'or'
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'in'
  | 'between'
  | 'days_of_week'

export type Filter<T = FilterType> = T extends 'in'
  ? { type: 'in'; key: string; values: string[] }
  : T extends 'and'
  ? { type: 'and'; conditions: Filter[] }
  : T extends 'or'
  ? { type: 'or'; conditions: Filter[] }
  : T extends 'eq'
  ? { type: 'eq'; key: string; value: string }
  : T extends 'neq'
  ? { type: 'neq'; key: string; value: string }
  : T extends 'gt'
  ? { type: 'gt'; key: string; value: string }
  : T extends 'gte'
  ? { type: 'gte'; key: string; value: string }
  : T extends 'lt'
  ? { type: 'lt'; key: string; value: string }
  : T extends 'lte'
  ? { type: 'lte'; key: string; value: string }
  : T extends 'between'
  ? { type: 'between'; key: string; range: [any, any] }
  : T extends 'days_of_week'
  ? { type: 'days_of_week'; days: number[] }
  : never

export type OrderBy = {
  field: string
  direction: 'ASC' | 'DESC'
  nulls?: 'FIRST' | 'LAST'
}

export type Query = {
  measurement: string
  start: string
  end: string
  tags?: string[]
  fields?: string[]
  pathQuery: string
  filters?: Filter
  orderBy?: OrderBy[]
  limit?: number
}

export type GroupBy =
  | {
      type: 'timestamp'
    }
  | {
      type: 'path'
    }
  | {
      type: 'timestamp_bucket'
      interval: string
    }
  | {
      type: 'path_subtree'
      depth: number
    }
  | {
      type: 'tag'
      tag: string
    }
  | {
      type: 'timestamp_extract'
      extract:
        | 'YEAR'
        | 'MONTH'
        | 'DAY'
        | 'HOUR'
        | 'MINUTE'
        | 'DOW'
        | 'DOY'
        | 'WEEK'
        | 'QUARTER'
        | 'ISODOW'
    }

export type ExpressionType =
  | 'field'
  | 'sum'
  | 'avg'
  | 'min'
  | 'max'
  | 'stddev'
  | 'histogram'
  | 'last'
  | 'first'
  | 'add'
  | 'sub'
  | 'mul'
  | 'div'
  | 'number'

export type Expression<T extends ExpressionType = ExpressionType> = T extends 'field'
  ? { type: 'field'; name: string }
  : T extends 'sum'
  ? { type: 'sum'; exp: Expression }
  : T extends 'avg'
  ? { type: 'avg'; exp: Expression }
  : T extends 'min'
  ? { type: 'min'; exp: Expression }
  : T extends 'max'
  ? { type: 'max'; exp: Expression }
  : T extends 'stddev'
  ? { type: 'stddev'; exp: Expression }
  : T extends 'histogram'
  ? { type: 'histogram'; exp: Expression; min: number; max: number; nbuckets: number }
  : T extends 'last'
  ? { type: 'last'; exp: Expression }
  : T extends 'first'
  ? { type: 'first'; exp: Expression }
  : T extends 'add'
  ? { type: 'add'; exps: Expression[] }
  : T extends 'sub'
  ? { type: 'sub'; exps: Expression[] }
  : T extends 'mul'
  ? { type: 'mul'; exps: Expression[] }
  : T extends 'div'
  ? { type: 'div'; exps: Expression[] }
  : T extends 'number'
  ? { type: 'number'; value: number }
  : never

export function mapExpression(expr: Expression, fn: (e: Expression) => Expression): Expression {
  const mapped = fn(expr)

  switch (mapped.type) {
    case 'field':
    case 'number':
      return mapped
    case 'sum':
    case 'avg':
    case 'min':
    case 'max':
    case 'stddev':
    case 'histogram':
    case 'last':
    case 'first':
      return {
        ...mapped,
        exp: mapExpression(mapped.exp, fn)
      }
    case 'add':
    case 'sub':
    case 'mul':
    case 'div':
      return {
        ...mapped,
        exps: mapped.exps.map((e) => mapExpression(e, fn))
      }
    default:
      throw new Error(`Unhandled expression type: ${(mapped as any).type}`)
  }
}

export type Aggregation<T extends ExpressionType = ExpressionType> = {
  name: string
  expression: Expression<T>
}

export type AggregationQuery = {
  query: Query
  groupBy: GroupBy[]
  aggregations: Aggregation[]
}

export type InfoValue = {
  key: string
  entities: string[]
  values: Record<string, number>
}

export type Information = {
  tags: InfoValue[]
  entityLevels: string[][]
}

export type MeasurementPoint = {
  groups: Record<string, string | number | null>
  timestamp?: string
  realmId: string
  path?: string
  tags: Record<string, string | null>
  fields: Record<string, number | string | number[] | null>
}

export type MeasurementPointTS = {
  timestamp: string
} & MeasurementPoint

export type MeasurementMeta = {
  name: string
  version: number
  tags: string[]
  fields: string[]
}
